/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/components/Layout/Layout.tsx
 * DESCRIPTION: Main layout component that wraps all protected routes.
 *              Provides a sidebar and header
 *              Provides persistent sidebar navigation and main content
 *              area for the application.
*               Handles mobile responsiveness and content area sizing.
 * FEATURES:
 * - Persistent sidebar navigation across all protected routes
 * - Main content area with proper spacing and overflow handling
 * - Responsive layout optimized for desktop
 * - Mobile-responsive content area
 * - Sidebar offset on desktop (250px)
 * - Full viewport height support
 * - Smooth transitions when sidebar toggles
 * - Landscape orientation optimization
 * - iOS viewport fix
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-03-26
 * @updated 2026-03-26 - Added Header component, removed duplicate code from pages
 * ─────────────────────────────────────────────────────────────────────
 */
/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/components/Layout/Layout.tsx
 * DESCRIPTION: Main layout wrapper with sidebar and header.
 *              Desktop: Sidebar fixed at 72px (collapsed) or 280px (expanded).
 *                       Main content starts at left edge (no margin offset needed
 *                       since sidebar is fixed position).
 *              Mobile: Sidebar overlays when toggled.
 *
 * KEY FIXES:
 * - Desktop: Sidebar is fixed position (doesn't affect document flow)
 * - Desktop: Main content does NOT need margin offset (sidebar is fixed)
 * - Mobile: Full overlay behavior preserved
 * - Ensures sidebar icons are ALWAYS visible on desktop (72px minimum)
 * - Prevents content from being pushed off-screen right
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 3.1.1
 * @created 2025-01-15
 * @updated 2026-04-03 - Fixed desktop overflow issue, kept mobile
 *                       collapsible sidebar functionality
 * @updated 2026-04-14 - Fixed desktop visibility: removed lg:ml-[72px]
 *                       since sidebar is fixed position (doesn't affect flow),
 *                       content now uses full width without offset
 * ─────────────────────────────────────────────────────────────────────
 */
import { ReactNode } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar - Fixed position, 72px when collapsed, 280px when expanded */}
      {/* Does NOT affect document flow (fixed positioning) */}
      <Sidebar />

      {/* Main Content Area */}
      {/* NO margin offset needed - sidebar is fixed position */}
      {/* Content starts at left edge, sidebar overlays on top */}
      <div className="w-full min-h-screen">
        {/* Header */}
        <Header />

        {/* Page Content */}
        {/* Use max-w-7xl to prevent content from stretching too wide on large screens */}
        <main className="p-4 lg:p-8 w-full">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
